import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import TimeRangeSelector from "../TimeRangeSelector";
import {
  ArrowUpRightIcon,
  CloseIcon,
  WalletIcon,
  TransferIcon,
  SwapIcon,
} from "../../icons";
import AssetDetailChart from "./AssetDetailChart";
import { formatCryptoAmount, formatMarketPrice, formatShortUsd } from "../../utils/format";
import { useSparklineChartData } from "../../hooks/useSparklineChartData";
import AssetDetailChartSkeleton from "../../../components/Skeleton/AssetDetailChartSkeleton";
import { getCryptoTransactions } from "../../../redux/actions/Transaction/TransactionHistory";
import { useDispatch, useSelector } from "react-redux";
import TransactionListItem from "../TransactionHistory/TransactionListItem";
import EmptyNewCryptoTransaction from "../../../components/Empty/EmptyNewCryptoTransaction";
import { TransactionListSkeleton } from "../../../components/Skeleton/TransactionHistorySkeleton";
import { useNavigate } from "react-router-dom";

const ModalPercentChange = ({ value, positive }) => (
  <div
    className={`inline-flex items-center ${
      positive ? "text-[#4ab932]" : "text-error-500"
    }`}
  >
    <span className={positive ? "" : "rotate-180"}>
      <ArrowUpRightIcon size={14} />
    </span>
    <span className="text-[10px] font-semibold leading-[15px]">
      {Math.abs(value).toFixed(2)}%
    </span>
  </div>
);

const StatBox = ({ label, value, change, changePositive, className = "" }) => (
  <div
    className={`flex h-[57px] flex-col items-center justify-center rounded-[8px] border border-divider bg-background-surface p-px ${className}`}
  >
    <div className="flex flex-col items-center gap-1">
      <p className="whitespace-nowrap text-[10px] font-medium leading-4 text-text-secondary">
        {label}
      </p>
      <div className="flex items-center justify-center gap-1 pb-1">
        <p className="whitespace-nowrap text-sm font-semibold leading-[19.5px] text-text-primary">
          {value}
        </p>
        {change != null && (
          <ModalPercentChange value={change} positive={changePositive} />
        )}
      </div>
    </div>
  </div>
);


const MarketPriceDetailModal = ({
  asset,
  isOpen,
  onClose,
  onBuySell,
  onTransfer,
  onSwap,
}) => {
  const [timeRange, setTimeRange] = useState("24H");
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const { loading, error, chartData } = useSparklineChartData(asset?.symbol, timeRange);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state.userInfo?.userInfo);
  const cryptoAccounts = useSelector((state) => state.client?.clientInfo?.crypto_accounts ?? []);

  const account = useMemo(() => cryptoAccounts.find(({ currency_code }) => 
    currency_code?.toUpperCase() === asset?.symbol?.toUpperCase()),
  [cryptoAccounts, asset?.symbol]);

  useEffect(() => {
    if (!isOpen) return;

    if (!userInfo?.client_id || !account?.id) {
      setTransactions([]);
      return;
    }

    const fetchTransactions = async () => {
      try {
        setTransactionsLoading(true);

        const response = await dispatch(
          getCryptoTransactions({
            client_id: userInfo.client_id,
            account_id: account.id,
            status: "Approved",
            limit: 5,
          })
        );

        setTransactions(response?.data ?? []);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
        setTransactions([]);
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchTransactions();
  }, [
    isOpen,
    dispatch,
    userInfo?.client_id,
    account?.id,
  ]);

  if (!asset || !chartData) return null;
  
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-[3px]" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden p-0 sm:p-5">
          <div className="flex min-h-full items-end justify-center sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
            >
              <Dialog.Panel className="relative flex w-full max-w-[1141px] flex-col sm:gap-3">
                <div className="flex w-full flex-col items-center sm:hidden">
                  <button
                    type="button"
                    onClick={onClose}
                    className="mb-4 flex size-10 shrink-0 items-center justify-center rounded-full bg-[#9CA3AF] shadow-[0_2px_8px_rgba(0,0,0,0.15)] custom-transition hover:bg-[#8B939E]"
                    aria-label="Close"
                  >
                    <CloseIcon className="text-white" />
                  </button>
                </div>

                <div className="relative flex max-h-[85dvh] w-full flex-col overflow-hidden rounded-t-[24px] bg-background-surface shadow-[0_4px_24px_rgba(0,0,0,0.08)] sm:max-h-none sm:rounded-[16px]">
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-6 top-6 z-10 hidden rounded-full p-1 custom-transition hover:bg-option-hover sm:block"
                    aria-label="Close"
                  >
                    <CloseIcon />
                  </button>

                  <div className="min-h-0 flex-1 overflow-y-auto hide-scrollbar">
                    <div className="flex flex-col gap-6 p-5 lg:grid lg:grid-cols-[minmax(0,406px)_1fr] lg:gap-8 lg:p-5">
                      <div className="order-1 flex flex-col gap-4 lg:col-start-1 lg:row-start-1">
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2">
                              <div className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full">
                                <img
                                  src={asset.logo}
                                  alt={asset.name}
                                  className="size-6 rounded-full object-cover"
                                />
                              </div>
                              <span className="text-base font-semibold leading-[19.5px] text-text-primary">
                                {asset.name}
                              </span>
                            </div>
                            <span className="text-sm font-medium leading-[19.5px] text-text-secondary">
                              {asset.symbol} Price
                            </span>
                            <span className="rounded-[8px] bg-background-alt px-2 py-0.5 text-xs font-medium leading-[21px] text-text-secondary">
                              #{asset.rank}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-[36px] font-semibold leading-[48px] tracking-[-2px] text-[#4ab932]">
                              {formatMarketPrice(asset.price)}
                            </p>
                            <div
                              className={`inline-flex items-center text-xs font-semibold leading-[19.5px] ${
                                asset.change24h ? "text-[#4ab932]" : "text-error-500" 
                              }`}
                            >
                              <span className={asset.change24h ? "" : "rotate-90"}>
                                <ArrowUpRightIcon />
                              </span>
                              {asset.change24h.toFixed(2)}% (24H)
                            </div>
                          </div>
                        </div>

                        <div className="rounded-[8px] border border-divider bg-background-surface p-4">
                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1">
                              <p className="text-[10px] font-medium leading-4 text-text-secondary">
                                Your Balance
                              </p>
                              {account ? 
                                <>
                                  <p className="text-2xl font-semibold leading-6 text-text-primary">
                                    {formatCryptoAmount(account.balance ?? 0)} {account.currency_code ?? asset?.symbol}
                                  </p>
                                  <p className="text-xs font-medium leading-[21px] text-text-secondary">
                                    {formatCryptoAmount(account.balance * account.unit_price_usd) ?? "0.00" } USD
                                  </p>
                                </>
                                :
                                <>
                                  <p className="text-2xl font-semibold leading-6 text-text-primary">
                                    0.00 {asset?.symbol}
                                  </p>
                                  <p className="text-xs font-medium leading-[21px] text-text-secondary">
                                    0.00 USD
                                  </p>
                                </>
                              }
                            </div>

                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={onBuySell}
                                className="flex h-10 w-full items-center justify-center gap-[7px] rounded-[8px] bg-[#4ab932] px-4 text-sm font-medium leading-[19.5px] text-white custom-transition hover:bg-[#3da028]"
                              >
                                <span className="size-5 shrink-0 [&_svg]:size-5">
                                  <WalletIcon className="w-[18px] h-[18px]" />
                                </span>
                                Buy &amp; Sell
                              </button>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={onTransfer}
                                  className="flex h-10 items-center justify-center gap-[7px] rounded-[8px] border border-[#4ab932] bg-background-surface px-4 text-sm font-medium leading-[19.5px] text-text-primary custom-transition hover:bg-option-hover"
                                >
                                  <span className="size-5 shrink-0 text-[#4ab932] [&_svg]:size-5">
                                    <TransferIcon />
                                  </span>
                                  Transfer
                                </button>
                                <button
                                  type="button"
                                  onClick={onSwap}
                                  className="flex h-10 items-center justify-center gap-[7px] rounded-[8px] border border-[#4ab932] bg-background-surface px-4 text-sm font-medium leading-[19.5px] text-text-primary custom-transition hover:bg-option-hover"
                                >
                                  <span className="size-5 shrink-0 text-[#4ab932] [&_svg]:size-5">
                                    <SwapIcon />
                                  </span>
                                  Swap
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="order-2 flex min-w-0 flex-col gap-4 lg:col-start-2 lg:row-start-1">
                        <div className="flex justify-end sm:pr-12">
                          <TimeRangeSelector
                            active={timeRange}
                            onChange={setTimeRange}
                          />
                        </div>

                        {error && <p className="text-sm font-medium text-error-500">{error}</p>}

                        {loading ? 
                          <AssetDetailChartSkeleton /> 
                          : 
                          <AssetDetailChart
                            sparkline={chartData?.prices}
                            volumeUsd={chartData.total_volume}
                            timeRange={timeRange}
                          />
                        }

                        {/* <AssetDetailChartSkeleton /> */}

                      </div>

                     <div className="order-3 flex flex-col gap-4 lg:col-start-1 lg:row-start-2">
                      <div className="flex flex-col gap-2 md:pb-40">
                        <StatBox
                          label="Market Cap"
                          value={formatShortUsd(asset.marketCap)} // Outputs: $183.81B
                          change={asset?.raw?.market_cap_change_percentage_24h} // -0.04485
                          changePositive={asset?.raw?.market_cap_change_percentage_24h > 0} // false
                          className="w-full"
                        />

                        <div className="grid grid-cols-2 gap-2">
                          <StatBox
                            label="Volume (24h)"
                            value={formatShortUsd(asset.volumeUsd)} // Outputs: $41.59B
                            change={asset?.raw?.price_change_percentage_24h} // -0.01964
                            changePositive={asset?.raw?.price_change_percentage_24h > 0} // false
                          />
                          <StatBox
                            label="Vol/Mkt Cap (24h)"
                            value={`${((asset.volumeUsd / asset.marketCap) * 100).toFixed(2)}%`} // Outputs: 22.63%
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          <StatBox
                            label="Total Supply"
                            value={
                              asset?.raw?.total_supply
                                ? `${(asset.raw.total_supply / 1e9).toFixed(2)}B ${asset.symbol}` // Outputs: 189.52B USDT
                                : "—"
                            }
                          />
                          <StatBox
                            label="Max Supply"
                            value={asset?.raw?.max_supply ? `${(asset.raw.max_supply / 1e9).toFixed(2)}B ${asset.symbol}`: "—"}
                          />
                        </div>

                        <StatBox
                          label="Circulating Supply"
                          value={
                            asset?.raw?.circulating_supply
                              ? `${(asset.raw.circulating_supply / 1e9).toFixed(2)}B ${asset.symbol}` // Outputs: 184.05B USDT
                              : "—"
                          }
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="order-4 flex min-w-0 flex-col gap-4 lg:col-start-2 lg:row-start-2">
                      <div>
                        <div className="mb-2 flex items-center justify-between">
                          <h3 className="text-sm font-semibold leading-[21px] text-text-primary">
                            Recent Transactions
                          </h3>
                          <button
                            type="button"
                            onClick={() => navigate("/v2/crypto-transaction-history")}
                            className="text-xs font-semibold leading-[18px] text-text-secondary underline custom-transition hover:text-text-primary"
                          >
                            See all
                          </button>
                        </div>

                        <div className="max-h-[396px] h-full overflow-y-auto rounded-[16px] bg-background-surface p-px">
                          <div className="flex flex-col gap-2">
                            {transactionsLoading ? 
                              <TransactionListSkeleton limit={5}/>
                              :
                              <>
                                {transactions.length === 0 ? (
                                  <EmptyNewCryptoTransaction />
                                  ) : (
                                    <>
                                      {transactions.map((tx) => (
                                        <TransactionListItem
                                          key={tx.transaction_id}
                                          transaction={tx}
                                          isSelected={false}
                                          onSelect={() => true}
                                        />
                                      ))}
                                    </>
                                  )
                                }
                              </>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MarketPriceDetailModal;
